import { useState } from "react";
import { tasksApi, ApiError } from "@/api";
import type { JobApplication, JobTask, TaskCreateInput, TaskUpdateInput, TaskPriority, TaskStatus } from "@/types";
import { TASK_PRIORITIES, TASK_STATUSES } from "@/types";
import { Button, TextField, TextAreaField, SelectField } from "@/components/ui";
import { humanise } from "@/lib/jobs";
import { toDateInputValue } from "@/lib/format";
import { FormDrawer } from "./FormDrawer";
import { OwnerSelect, useStaffUsers } from "@/staff/tasks/OwnerSelect";

/** Create or edit a task. On success the parent re-fetches /detail (the server
 *  recomputes deadlines/work_items), so this drawer just closes. */
export function TaskDrawer({
  jobId,
  task,
  applications,
  onClose,
  onSaved,
}: {
  jobId: number;
  task: JobTask | null; // null = create
  applications: JobApplication[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = task != null;
  const [title, setTitle] = useState(task?.title ?? "");
  const [description, setDescription] = useState(task?.description ?? "");
  const [priority, setPriority] = useState<TaskPriority>((task?.priority as TaskPriority) || "medium");
  const [status, setStatus] = useState<TaskStatus>((task?.status as TaskStatus) || "open");
  const [due, setDue] = useState(toDateInputValue(task?.due_date));
  const [appId, setAppId] = useState<number>(task?.application_id ?? 0);
  const [ownerId, setOwnerId] = useState<number>(task?.assigned_to_user_id ?? 0);
  const { staff, failed: staffFailed } = useStaffUsers();

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function del() {
    if (!task || saving || deleting) return;
    if (!window.confirm(`Delete the task “${task.title}”? This can't be undone.`)) return;
    setDeleting(true);
    setError(undefined);
    try {
      await tasksApi.updateTask({ id: task.id, deleted: true });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the task.");
      setDeleting(false);
    }
  }

  async function submit() {
    if (saving) return;
    if (!title.trim()) {
      setError("Give the task a title.");
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      if (isEdit && task) {
        const body: TaskUpdateInput = {
          id: task.id,
          title: title.trim(),
          description: description.trim(),
          priority,
          status,
          due_date: due || null,
          application_id: appId, // 0 clears the link
        };
        // Owner is only touched once the roster loaded — a failed load must
        // never clear an existing assignment.
        if (staff != null) {
          if (ownerId > 0) body.assigned_to_user_id = ownerId;
          else if (task.assigned_to_user_id) body.unassign = true;
        }
        await tasksApi.updateTask(body);
      } else {
        const body: TaskCreateInput = {
          job_id: jobId,
          title: title.trim(),
          description: description.trim() || undefined,
          priority,
          due_date: due || null,
        };
        if (appId > 0) body.application_id = appId;
        if (staff != null && ownerId > 0) body.assigned_to_user_id = ownerId;
        await tasksApi.createTask(body);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the task.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title={isEdit ? "Edit task" : "New task"}
      busy={saving || deleting}
      onClose={onClose}
      footer={
        <>
          {isEdit ? (
            <Button variant="danger" className="job-task-del" onClick={del} loading={deleting} disabled={saving}>Delete task</Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={saving || deleting}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving} disabled={deleting}>{isEdit ? "Save task" : "Add task"}</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <TextField label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextAreaField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
        <div className="lead-drawer__two">
          <SelectField label="Priority" value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>{humanise(p)}</option>
            ))}
          </SelectField>
          {isEdit ? (
            <SelectField label="Status" value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>{humanise(s)}</option>
              ))}
            </SelectField>
          ) : null}
        </div>
        <div className="lead-drawer__two">
          <TextField label="Due date" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          <OwnerSelect staff={staff} failed={staffFailed} value={ownerId} onChange={setOwnerId} />
        </div>
        {applications.length > 0 ? (
          <SelectField
            label="Link to application (optional)"
            value={String(appId)}
            onChange={(e) => setAppId(Number(e.target.value))}
          >
            <option value="0">None</option>
            {applications.map((a) => (
              <option key={a.id} value={a.id}>
                {a.app_reference || `Draft #${a.id}`}{a.application_type ? ` — ${humanise(a.application_type)}` : ""}
              </option>
            ))}
          </SelectField>
        ) : null}
      </section>
    </FormDrawer>
  );
}
